import Front from '@frontapp/plugin-sdk';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/*
 * Context.
 */

export const FrontContext = createContext();

/*
 * Hooks.
 */

/** Returns the raw Front SDK context (backward-compatible). */
export function useFrontContext() {
  const value = useContext(FrontContext);
  return value?.context;
}

/** Returns the error string if the Front SDK failed to connect. */
export function useFrontError() {
  const value = useContext(FrontContext);
  return value?.error ?? null;
}

/*
 * Component.
 */

const FRONT_SDK_TIMEOUT_MS = 10_000;

export const FrontContextProvider = ({ children }) => {
  const [context, setContext] = useState();
  const [error, setError] = useState(null);

  useEffect(() => {
    let subscription;
    let timeoutId;
    try {
      subscription = Front.contextUpdates.subscribe({
        next: (frontContext) => {
          clearTimeout(timeoutId);
          setError(null);
          setContext(frontContext);
        },
        error: (err) => {
          console.error('Front SDK subscription error:', err);
          setError('Failed to connect to Front.');
        },
      });
    } catch (err) {
      console.error('Front SDK initialization error:', err);
      setError('Failed to initialize Front plugin.');
      return undefined;
    }

    timeoutId = setTimeout(() => {
      setError((prev) => prev || 'Timed out waiting for Front context.');
    }, FRONT_SDK_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
      subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({ context, error }), [context, error]);

  return (
    <FrontContext.Provider value={value}>
      {children}
    </FrontContext.Provider>
  );
};
